import * as Sentry from '@sentry/nextjs';
import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';

import { getSearchFromPath } from '@/utils/urlUtils';

import { useRouter } from '@/hooks/useRouter';
import {
  EXPERIMENT_COOKIE_MAX_AGE_SECONDS,
  getClientPersistedCookieOptions,
  getExperimentCookieName,
} from '@/lib/experiments/experimentCookie';
import { ACTIVE_EXPERIMENTS } from '@/lib/experiments/experimentRegistry';
import { isProductionEnv } from '@/lib/experiments/isProductionEnv';
import { resolveExperiment } from '@/lib/experiments/resolveExperiment';
import type { ExperimentAssignments } from '@/lib/experiments/types';
import {
  readGeoCountryFromClientCookie,
  syncGeoCountryCookie,
} from '@/lib/geoCountry';
import { StorageHelper } from '@/lib/storage';

const ExperimentContext = createContext<ExperimentAssignments>({});

export type ExperimentProviderProps = {
  children: ReactNode;
  initialAssignments: ExperimentAssignments;
};

export const ExperimentProvider = ({
  children,
  initialAssignments,
}: ExperimentProviderProps) => {
  const router = useRouter();
  const [assignments, setAssignments] =
    useState<ExperimentAssignments>(initialAssignments);

  useEffect(() => {
    let cancelled = false;

    const syncAssignments = async () => {
      const storageHelper = new StorageHelper();
      const cookieOptions = getClientPersistedCookieOptions(
        EXPERIMENT_COOKIE_MAX_AGE_SECONDS,
      );
      const search = getSearchFromPath(router.asPath);
      let geoCountry = readGeoCountryFromClientCookie();

      if (!geoCountry) {
        geoCountry = await syncGeoCountryCookie();
      }

      if (cancelled) return;

      const isProduction = isProductionEnv();
      const localeSlug = router.asPath
        .split('?')[0]
        .split('/')
        .filter(Boolean)[0];
      const language = localeSlug?.split('-')[1];

      setAssignments((previous) => {
        const effective: ExperimentAssignments = {};

        for (const experiment of ACTIVE_EXPERIMENTS.filter(
          (item) => item.shipped,
        )) {
          const cookieName = getExperimentCookieName(
            experiment.name,
            experiment.version,
          );
          const result = resolveExperiment(experiment, {
            geoCountry,
            language,
            isBot: false,
            isProduction,
            urlSearch: search,
            getCookie: (name) => storageHelper.getCookie(name),
            random: Math.random,
          });

          effective[experiment.name] = result.variant;

          if (result.clearCookie) {
            storageHelper.removeCookie(cookieName, cookieOptions);
            continue;
          }

          if (result.setCookie) {
            storageHelper.setCookie(cookieName, result.variant, cookieOptions);
          }
        }

        const unchanged = ACTIVE_EXPERIMENTS.every(
          (experiment) =>
            effective[experiment.name] === previous[experiment.name],
        );

        return unchanged ? previous : effective;
      });
    };

    void syncAssignments();

    return () => {
      cancelled = true;
    };
  }, [router.asPath]);

  useEffect(() => {
    Sentry.setContext('experiments', assignments);
  }, [assignments]);

  return (
    <ExperimentContext.Provider value={assignments}>
      {children}
    </ExperimentContext.Provider>
  );
};

export const useExperimentAssignments = (): ExperimentAssignments =>
  useContext(ExperimentContext);
